(() => {
  const GIFT_CARD_PRODUCT_HANDLE =
    'get-to-free-shipping-gift-card-1';

  const OFFER_SELECTOR =
    '[data-bonus-gift-card-offer]';

  const STYLE_ID =
    'bonus-gift-card-offer-styles';

  const REOPEN_CART_KEY =
    'openMiniCartAfterGiftCardAdd';

  const CART_RECHECK_DELAY = 350;

  let giftCardVariants = {};
  let scheduled = false;
  let addInProgress = false;
  let cartCheckInProgress = false;
  let cartRecheckTimer = null;

  function addOfferStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');

    style.id = STYLE_ID;

    style.textContent = `
      .bonus-gift-card-offer {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
        margin: 8px 0;
        padding: 8px 9px;
        border: 1px solid rgba(
          var(--color-foreground, 18, 18, 18),
          0.14
        );
        border-radius: 4px;
        background: rgba(
          var(--color-foreground, 18, 18, 18),
          0.035
        );
        color: rgb(
          var(--color-foreground, 18, 18, 18)
        );
      }

      .bonus-gift-card-offer__icon {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: rgb(
          var(--color-button, 18, 18, 18)
        );
        color: rgb(
          var(--color-button-text, 255, 255, 255)
        );
      }

      .bonus-gift-card-offer__icon svg {
        width: 14px;
        height: 14px;
      }

      .bonus-gift-card-offer__content {
        display: flex;
        flex: 0 1 auto;
        align-items: center;
        gap: 7px;
        min-width: 0;
      }

      .bonus-gift-card-offer__message {
        flex: 0 1 auto;
        min-width: 0;
        margin: 0;
        font-size: 12px;
        line-height: 1.35;
      }

      .bonus-gift-card-offer__message strong {
        font-weight: 700;
      }

      .bonus-gift-card-offer__button {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        min-height: 32px;
        padding: 6px 10px;
        border: 1px solid rgb(
          var(--color-button, 18, 18, 18)
        );
        border-radius: 3px;
        background: rgb(
          var(--color-button, 18, 18, 18)
        );
        color: rgb(
          var(--color-button-text, 255, 255, 255)
        );
        font-family: inherit;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        line-height: 1;
        white-space: nowrap;
        text-transform: uppercase;
        cursor: pointer;
        transition: opacity 150ms ease;
      }

      .bonus-gift-card-offer__button:hover:not(:disabled) {
        opacity: 0.82;
      }

      .bonus-gift-card-offer__button:disabled {
        cursor: wait;
        opacity: 0.6;
      }

      .bonus-gift-card-offer__status {
        display: none;
      }

      @media screen and (max-width: 520px) {
        .bonus-gift-card-offer {
          gap: 6px;
        }

        .bonus-gift-card-offer__icon {
          display: none;
        }

        .bonus-gift-card-offer__content {
          width: 100%;
          gap: 6px;
        }

        .bonus-gift-card-offer__message {
          flex: 1 1 auto;
          font-size: 11px;
        }

        .bonus-gift-card-offer__button {
          min-height: 30px;
          padding: 6px 8px;
          font-size: 9px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function getTier(amountRemaining) {
    const tiers = [5, 10, 15, 20, 25, 30];

    return (
      tiers.find((tier) => tier >= amountRemaining) ||
      null
    );
  }

  async function loadGiftCardVariants() {
    if (Object.keys(giftCardVariants).length) {
      return giftCardVariants;
    }

    const response = await fetch(
      `${window.Shopify.routes.root}products/${GIFT_CARD_PRODUCT_HANDLE}.js`,
      {
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to load gift card options.'
      );
    }

    const product = await response.json();

    const valuesToPaidAmounts = {
      '5.50': 5,
      '11.00': 10,
      '16.50': 15,
      '22.00': 20,
      '27.50': 25,
      '33.00': 30
    };

    product.variants.forEach((variant) => {
      const numericTitle = parseFloat(
        String(variant.title).replace(/[^0-9.]/g, '')
      );

      if (!Number.isFinite(numericTitle)) return;

      const paidAmount =
        valuesToPaidAmounts[numericTitle.toFixed(2)];

      if (paidAmount) {
        giftCardVariants[paidAmount] =
          String(variant.id);
      }
    });

    return giftCardVariants;
  }

  async function getCurrentCart() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to check the current cart.'
      );
    }

    return response.json();
  }

  function cartHasBonusGiftCard(cart) {
    if (!cart?.items?.length) return false;

    const promotionalVariantIds = new Set(
      Object.values(giftCardVariants).map(String)
    );

    return cart.items.some((item) => {
      const itemHandle = String(
        item.handle ||
        item.product_handle ||
        ''
      );

      const itemVariantId = String(
        item.variant_id ||
        item.id ||
        ''
      );

      return (
        itemHandle === GIFT_CARD_PRODUCT_HANDLE ||
        promotionalVariantIds.has(itemVariantId)
      );
    });
  }

  function findShippingMessage() {
    return [...document.querySelectorAll('body *')].find(
      (element) => {
        const text =
          element.textContent?.trim() || '';

        return (
          element.children.length === 0 &&
          /spend\s+\$?[\d,.]+\s+more\s+for\s+free\s+shipping/i.test(
            text
          )
        );
      }
    );
  }

  function getRemainingAmount(messageElement) {
    const text = messageElement.textContent || '';

    const match = text.match(
      /\$?([\d,.]+)\s+more/i
    );

    if (!match) return null;

    return Number(
      match[1].replace(/,/g, '')
    );
  }

  function removeOffer() {
    document
      .querySelectorAll(OFFER_SELECTOR)
      .forEach((offer) => offer.remove());
  }

  function findCartDrawer() {
    return (
      document.querySelector('cart-drawer') ||
      document.querySelector('mini-cart') ||
      document.querySelector('[data-cart-drawer]') ||
      document.querySelector('[data-cart="drawer"]') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('#CartDrawer')
    );
  }

  function findCartTrigger() {
    return (
      document.querySelector('#cart-icon-bubble') ||
      document.querySelector('a[href="/cart"][aria-controls]') ||
      document.querySelector('[aria-controls="CartDrawer"]') ||
      document.querySelector('[aria-controls*="cart-drawer" i]') ||
      document.querySelector('[data-cart-toggle]') ||
      document.querySelector('[data-open-cart]') ||
      document.querySelector('.header__icon--cart')
    );
  }

  function cartDrawerLooksOpen() {
    const drawer = findCartDrawer();

    if (!drawer) return false;

    return (
      drawer.hasAttribute('open') ||
      drawer.classList.contains('active') ||
      drawer.classList.contains('is-open') ||
      drawer.classList.contains('open') ||
      drawer.getAttribute('aria-hidden') === 'false'
    );
  }

  function openMiniCart() {
    const cartDrawer = findCartDrawer();

    if (
      cartDrawer &&
      typeof cartDrawer.open === 'function'
    ) {
      cartDrawer.open();
      return true;
    }

    if (
      cartDrawer &&
      typeof cartDrawer.show === 'function'
    ) {
      cartDrawer.show();
      return true;
    }

    const cartTrigger = findCartTrigger();

    if (cartTrigger) {
      cartTrigger.click();
      return true;
    }

    if (cartDrawer) {
      cartDrawer.classList.add(
        'active',
        'animate',
        'is-open',
        'open'
      );

      cartDrawer.setAttribute('open', '');
      cartDrawer.setAttribute(
        'aria-hidden',
        'false'
      );

      document.body.classList.add(
        'overflow-hidden',
        'cart-drawer-open'
      );

      return true;
    }

    return false;
  }

  function reopenMiniCartAfterRefresh() {
    const shouldOpen =
      sessionStorage.getItem(REOPEN_CART_KEY) ===
      'true';

    if (!shouldOpen) return;

    let attempts = 0;
    const maximumAttempts = 12;

    const tryOpeningCart = () => {
      attempts += 1;

      if (cartDrawerLooksOpen()) {
        sessionStorage.removeItem(REOPEN_CART_KEY);
        return;
      }

      openMiniCart();

      setTimeout(() => {
        if (cartDrawerLooksOpen()) {
          sessionStorage.removeItem(REOPEN_CART_KEY);
          return;
        }

        if (attempts < maximumAttempts) {
          setTimeout(tryOpeningCart, 250);
          return;
        }

        sessionStorage.removeItem(REOPEN_CART_KEY);
      }, 150);
    };

    setTimeout(tryOpeningCart, 350);
  }

  function scheduleOfferCheck(delay = CART_RECHECK_DELAY) {
    clearTimeout(cartRecheckTimer);

    cartRecheckTimer = setTimeout(() => {
      cartCheckInProgress = false;
      insertOffer();
    }, delay);
  }

  async function addBonusGiftCard(
    variantId,
    button
  ) {
    if (addInProgress) return;

    addInProgress = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'CHECKING...';

    try {
      await loadGiftCardVariants();

      const currentCart = await getCurrentCart();

      if (cartHasBonusGiftCard(currentCart)) {
        removeOffer();

        throw new Error(
          'Only one promotional gift card may be added per order.'
        );
      }

      button.textContent = 'ADDING...';

      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1,
                properties: {
                  '_Bonus gift card offer':
                    'Free shipping promotion'
                }
              }
            ]
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Unable to add the gift card.'
        );
      }

      button.textContent = 'ADDED ✓';

      sessionStorage.setItem(
        REOPEN_CART_KEY,
        'true'
      );

      window.location.reload();
    } catch (error) {
      console.error(
        'Gift card add error:',
        error
      );

      if (
        /only one promotional gift card/i.test(
          error.message || ''
        )
      ) {
        removeOffer();
        addInProgress = false;
        return;
      }

      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = 'TRY AGAIN';
      addInProgress = false;
    }
  }

  function renderOffer({
    shippingMessage,
    tier,
    variantId,
    amountRemaining
  }) {
    const existingOffer =
      document.querySelector(OFFER_SELECTOR);

    if (
      existingOffer &&
      Number(existingOffer.dataset.tier) === tier
    ) {
      return;
    }

    if (addInProgress) return;

    removeOffer();

    const giftCardValue = tier * 1.1;
    const offer =
      document.createElement('div');

    offer.dataset.bonusGiftCardOffer = 'true';
    offer.dataset.tier = String(tier);
    offer.className =
      'bonus-gift-card-offer';

    offer.innerHTML = `
      <span
        class="bonus-gift-card-offer__icon"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="3"
            y="6"
            width="18"
            height="15"
            rx="2"
          ></rect>

          <path d="M3 10h18"></path>
          <path d="M12 6v15"></path>

          <path
            d="M12 6H8.5a2.5 2.5 0 1 1 0-5C11 1 12 6 12 6Z"
          ></path>

          <path
            d="M12 6h3.5a2.5 2.5 0 1 0 0-5C13 1 12 6 12 6Z"
          ></path>
        </svg>
      </span>

      <div class="bonus-gift-card-offer__content">
        <p class="bonus-gift-card-offer__message">
          <strong>
            $${amountRemaining.toFixed(2)} from free shipping.
          </strong>
          Add $${giftCardValue.toFixed(2)} gift card for $${tier.toFixed(2)} to use
          on a future purchase.
        </p>

        <button
          type="button"
          class="bonus-gift-card-offer__button"
        >
          ADD GIFT CARD
        </button>
      </div>

      <span
        class="bonus-gift-card-offer__status"
        role="status"
        aria-live="polite"
      ></span>
    `;

    const button = offer.querySelector(
      '.bonus-gift-card-offer__button'
    );

    button.addEventListener('click', () => {
      addBonusGiftCard(
        variantId,
        button
      );
    });

    const bannerContainer =
      shippingMessage.parentElement ||
      shippingMessage;

    bannerContainer.insertAdjacentElement(
      'afterend',
      offer
    );
  }

  async function insertOffer() {
    addOfferStyles();

    if (
      addInProgress ||
      cartCheckInProgress
    ) {
      return;
    }

    cartCheckInProgress = true;

    try {
      const shippingMessage =
        findShippingMessage();

      if (!shippingMessage) {
        removeOffer();
        return;
      }

      const amountRemaining =
        getRemainingAmount(shippingMessage);

      if (
        amountRemaining === null ||
        amountRemaining <= 0 ||
        amountRemaining > 30
      ) {
        removeOffer();
        return;
      }

      const tier =
        getTier(amountRemaining);

      if (!tier) {
        removeOffer();
        return;
      }

      const variants =
        await loadGiftCardVariants();

      const cart =
        await getCurrentCart();

      /*
       * If the promotional gift card remains in the cart,
       * keep the offer hidden.
       *
       * When the gift card is removed and the cart falls
       * below $50, this check becomes false and the offer
       * is rendered again.
       */
      if (cartHasBonusGiftCard(cart)) {
        removeOffer();
        return;
      }

      const variantId =
        variants[tier];

      if (!variantId) {
        console.warn(
          `No gift card variant found for the $${tier} tier.`
        );

        removeOffer();
        return;
      }

      renderOffer({
        shippingMessage,
        tier,
        variantId,
        amountRemaining
      });
    } catch (error) {
      console.error(
        'Gift card offer error:',
        error
      );

      removeOffer();
    } finally {
      cartCheckInProgress = false;
    }
  }

  function handlePossibleCartChange(event) {
    const target = event.target;

    if (
      target?.closest?.(
        [
          'cart-remove-button',
          '[data-cart-remove]',
          '[data-remove-item]',
          '.cart-remove-button',
          '.cart-item__remove',
          '.quantity__button',
          '[name="updates[]"]',
          'input[name^="updates"]',
          'button[name="minus"]',
          'button[name="plus"]'
        ].join(',')
      )
    ) {
      scheduleOfferCheck(500);
      scheduleOfferCheck(1200);
    }
  }

  const observer =
    new MutationObserver(() => {
      if (
        scheduled ||
        addInProgress ||
        cartCheckInProgress
      ) {
        return;
      }

      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        scheduleOfferCheck(200);
      });
    });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });

  /*
   * Re-check after common Shopify and theme cart events.
   */
  [
    'cart:updated',
    'cart:update',
    'cart:refresh',
    'cart:change',
    'cart:changed',
    'cart:item-removed',
    'cart:item-updated',
    'ajaxProduct:added'
  ].forEach((eventName) => {
    document.addEventListener(
      eventName,
      () => scheduleOfferCheck()
    );

    window.addEventListener(
      eventName,
      () => scheduleOfferCheck()
    );
  });

  /*
   * Catch quantity and remove actions even when the theme
   * does not dispatch a cart event.
   */
  document.addEventListener(
    'click',
    handlePossibleCartChange
  );

  document.addEventListener(
    'change',
    handlePossibleCartChange
  );

  document.addEventListener(
    'input',
    handlePossibleCartChange
  );

  /*
   * Re-check when returning to the tab, such as after the
   * cart was changed in another browser tab.
   */
  document.addEventListener(
    'visibilitychange',
    () => {
      if (!document.hidden) {
        scheduleOfferCheck(100);
      }
    }
  );

  window.addEventListener(
    'pageshow',
    () => scheduleOfferCheck(100)
  );

  window.addEventListener(
    'focus',
    () => scheduleOfferCheck(100)
  );

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      addOfferStyles();
      reopenMiniCartAfterRefresh();
      insertOffer();
    }
  );

  addOfferStyles();
  reopenMiniCartAfterRefresh();
  insertOffer();
})();